// The colours a note channel can have. LCCRM-87.
//
// A fixed palette, picked in two steps: a hue, then one of that hue's shades.
// The stored value is the hex itself (the database checks its shape), so a
// label needs no lookup to draw, and the palette can grow without a migration.
// The hexes are Tailwind's 200 to 700, written out: a class name built at run
// time (`bg-${hue}-500`) is one Tailwind never generates.

export type ChannelHue = {
  id: string;
  name: string;
  /** Lightest to darkest. */
  shades: readonly string[];
};

export const SHADE_NAMES = [
  "Pale",
  "Light",
  "Soft",
  "Bright",
  "Strong",
  "Deep",
] as const;

// The shade that stands for a hue in the first step, and the one a hue starts
// on in the second.
export const DEFAULT_SHADE = 3;

export const CHANNEL_HUES: readonly ChannelHue[] = [
  {
    id: "red",
    name: "Red",
    shades: ["#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c"],
  },
  {
    id: "orange",
    name: "Orange",
    shades: ["#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c"],
  },
  {
    id: "amber",
    name: "Amber",
    shades: ["#fde68a", "#fcd34d", "#fbbf24", "#f59e0b", "#d97706", "#b45309"],
  },
  {
    id: "lime",
    name: "Lime",
    shades: ["#d9f99d", "#bef264", "#a3e635", "#84cc16", "#65a30d", "#4d7c0f"],
  },
  {
    id: "green",
    name: "Green",
    shades: ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d"],
  },
  {
    id: "teal",
    name: "Teal",
    shades: ["#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e"],
  },
  {
    id: "sky",
    name: "Sky",
    shades: ["#bae6fd", "#7dd3fc", "#38bdf8", "#0ea5e9", "#0284c7", "#0369a1"],
  },
  {
    id: "blue",
    name: "Blue",
    shades: ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8"],
  },
  {
    id: "indigo",
    name: "Indigo",
    shades: ["#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#4338ca"],
  },
  {
    id: "violet",
    name: "Violet",
    shades: ["#ddd6fe", "#c4b5fd", "#a78bfa", "#8b5cf6", "#7c3aed", "#6d28d9"],
  },
  {
    id: "pink",
    name: "Pink",
    shades: ["#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d"],
  },
  {
    id: "slate",
    name: "Slate",
    shades: ["#e2e8f0", "#cbd5e1", "#94a3b8", "#64748b", "#475569", "#334155"],
  },
];

/** The hue a stored colour belongs to; undefined for none or one not in the palette. */
export const hueOfColor = (
  color: string | null | undefined,
): ChannelHue | undefined => {
  if (!color) return undefined;
  const wanted = color.toLowerCase();
  return CHANNEL_HUES.find((hue) => hue.shades.includes(wanted));
};

/**
 * The inline style of a channel label: outlined in its colour, with a faint
 * tint of it behind. The text stays the theme's own, so every shade reads in
 * both themes. Undefined leaves the plain outline.
 *
 * `tint` is how much of the colour the background takes, in percent: a whole
 * note card wants less of it than a small label does.
 */
export const channelColorStyle = (
  color: string | null | undefined,
  tint = 12,
): { borderColor: string; backgroundColor: string } | undefined =>
  color && /^#[0-9a-f]{6}$/i.test(color)
    ? {
        borderColor: color,
        backgroundColor: `color-mix(in srgb, ${color} ${tint}%, transparent)`,
      }
    : undefined;
